import { computed, inject, Injectable, signal } from '@angular/core';
import { finalize } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { PaginatedResponse } from '@/shared/models/paginated-response.model';
import { HelpersService } from '@/shared/services/helpers.service';
import { FeedbackApiService } from './data-access/feedback-api.service';
import { CreateFeedbackPayload, FeedbackListItem, MyFeedbackListQuery } from './data-access/feedback.model';

const EMPTY_PAGE: PaginatedResponse<FeedbackListItem> = { items: [], total: 0, page: 1, pageSize: 10 };

@Injectable({
    providedIn: 'root'
})
export class FeedbackService {
    /** The page of the caller's own feedback currently on screen. */
    public readonly myFeedbackList = signal<PaginatedResponse<FeedbackListItem>>(EMPTY_PAGE);

    private readonly _listRequestsInFlight = signal<number>(0);
    public readonly listLoading = computed(() => this._listRequestsInFlight() > 0);

    public readonly submitting = signal<boolean>(false);

    private readonly _feedbackApiService = inject(FeedbackApiService);
    private readonly _helpersService = inject(HelpersService);
    private readonly _translateService = inject(TranslateService);

    public loadMyFeedback(query: MyFeedbackListQuery): void {
        this._listRequestsInFlight.update((count) => count + 1);
        this._feedbackApiService
            .getMyFeedback(query)
            .pipe(finalize(() => this._listRequestsInFlight.update((count) => Math.max(0, count - 1))))
            .subscribe({
                next: (response) => this.myFeedbackList.set(response ?? { ...EMPTY_PAGE, page: query.page, pageSize: query.pageSize }),
                error: () => this.myFeedbackList.set({ ...EMPTY_PAGE, page: query.page, pageSize: query.pageSize })
            });
    }

    public submitFeedback(payload: CreateFeedbackPayload, onSuccess?: () => void): void {
        this.submitting.set(true);
        this._feedbackApiService
            .createFeedback(payload)
            .pipe(finalize(() => this.submitting.set(false)))
            .subscribe({
                next: () => {
                    this._helpersService.showSuccessMessage(this._translateService.instant('FEEDBACK.DIALOG.SUCCESS'));
                    onSuccess?.();
                },
                error: () => this._helpersService.showErrorMessage(this._translateService.instant('GENERIC.ERROR'))
            });
    }
}
